import { useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Facility, Page, PatientIndex } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, PageHeader, StatusBadge } from '../../ui/components';

const CLINICAL_STAFF_ROLES = ['CLINICIAN', 'RECEPTION', 'TREATMENT_BOARD'];

/**
 * Clinical-staff Master Patient Index lookup — GET /patients/:nin
 * (packages/modules/m24-ehealth/src/routes/patients.ts). Every lookup here
 * writes a HealthAccessLog row the citizen can read back themselves via
 * GET /me/access-log (this portal never surfaces that log — it's citizen
 * transparency, not a staff report — see this app's build report).
 */
export function Patients() {
  const { persona } = useAuth();
  const canLookUp = persona?.roles.some((r) => CLINICAL_STAFF_ROLES.includes(r)) ?? false;
  const [ninInput, setNinInput] = useState('');
  const [nin, setNin] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState<PatientIndex | null>(null);

  const lookUp = async () => {
    const target = ninInput.trim();
    if (!target) return;
    setBusy(true);
    setError(null);
    setNotFound(false);
    setProfile(null);
    try {
      const p = await api.get<PatientIndex>(`/api/v1/m24/patients/${encodeURIComponent(target)}`);
      setProfile(p);
      setNin(target);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'NOT_FOUND') {
        setNotFound(true);
        setNin(target);
      } else {
        setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Lookup failed');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Patient Lookup" subtitle="Master Patient Index — search by National Identification Number (NIN)." />

      {!canLookUp ? (
        <div className="mb-4">
          <ErrorNote message="Patient lookup requires CLINICIAN, RECEPTION or TREATMENT_BOARD — the API will reject this with 403." />
        </div>
      ) : null}

      <div className="card mb-5 p-5">
        <div className="flex gap-2">
          <input
            className="field-input"
            placeholder="NIN, e.g. SC870512033"
            value={ninInput}
            onChange={(e) => setNinInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && lookUp()}
          />
          <button className="btn-primary shrink-0" onClick={lookUp} disabled={busy || !ninInput.trim()}>
            {busy ? 'Looking up…' : 'Look up'}
          </button>
        </div>
        {error ? (
          <div className="mt-3">
            <ErrorNote message={error} />
          </div>
        ) : null}
        {notFound ? (
          <div className="mt-3 rounded-lg bg-slate-100 px-3.5 py-2.5 text-sm text-slate-600">
            No health profile on file for {nin} yet — one is created automatically the first time the citizen sets up their own profile, or the
            first time a facility HIS record is linked below.
          </div>
        ) : null}
      </div>

      {profile ? <ProfileCard profile={profile} onLinked={setProfile} /> : null}
      {notFound && nin ? <LinkHisPanel nin={nin} onLinked={setProfile} /> : null}
    </div>
  );
}

function ProfileCard({ profile, onLinked }: { profile: PatientIndex; onLinked: (p: PatientIndex) => void }) {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Health profile — {profile.nin}</h2>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-500">Blood group</dt>
            <dd className="font-medium text-slate-800">{profile.bloodGroup?.replaceAll('_', ' ') ?? '—'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Emergency contact</dt>
            <dd className="text-right font-medium text-slate-800">
              {profile.emergencyContact ? (
                <>
                  {profile.emergencyContact.name} ({profile.emergencyContact.relation})
                  <div className="text-xs font-normal text-slate-500">{profile.emergencyContact.phone}</div>
                </>
              ) : (
                '—'
              )}
            </dd>
          </div>
        </dl>
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Allergies</div>
          {profile.allergies.length === 0 ? (
            <div className="text-xs text-slate-400">None recorded.</div>
          ) : (
            <ul className="space-y-1">
              {profile.allergies.map((a, i) => (
                <li key={i} className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs">
                  <span className="font-medium text-slate-700">{a.substance}</span>
                  <StatusBadge status={a.severity} />
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="mt-3 text-[11px] text-slate-400">
          This view was just written to this patient's HealthAccessLog and is visible to them via their own citizen app.
        </p>
      </div>

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Linked HIS records</h2>
        {profile.hisIds.length === 0 ? (
          <div className="px-2 py-6 text-center text-sm text-slate-400">No facility HIS records linked yet.</div>
        ) : (
          <ul className="space-y-1.5">
            {profile.hisIds.map((l, i) => (
              <li key={i} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
                <div className="font-medium text-slate-800">{l.facilityName}</div>
                <div className="text-xs text-slate-500">HIS id: {l.hisId}</div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 border-t border-slate-100 pt-4">
          <LinkHisPanel nin={profile.nin} onLinked={onLinked} embedded />
        </div>
      </div>
    </div>
  );
}

function LinkHisPanel({ nin, onLinked, embedded }: { nin: string; onLinked: (p: PatientIndex) => void; embedded?: boolean }) {
  const facilitiesQ = useApiQuery<Page<Facility>>('/api/v1/m24/facilities?limit=100');
  const [facilityId, setFacilityId] = useState('');
  const [hisId, setHisId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  const facilities = facilitiesQ.data?.items ?? [];

  const link = async () => {
    setBusy(true);
    setError(null);
    setOk(false);
    try {
      const p = await api.post<PatientIndex>(`/api/v1/m24/patients/${encodeURIComponent(nin)}/his-links`, { facilityId, hisId });
      onLinked(p);
      setOk(true);
      setHisId('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to link HIS record');
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <div className="space-y-2">
      {error ? <ErrorNote message={error} /> : null}
      {ok ? <div className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">HIS record linked.</div> : null}
      <Field label="Facility">
        <select className="field-input" value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
          <option value="">Select a facility…</option>
          {facilities.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Facility HIS local patient id">
        <input className="field-input" value={hisId} onChange={(e) => setHisId(e.target.value)} placeholder="e.g. VH-2026-00417" />
      </Field>
      <button className="btn-secondary w-full" onClick={link} disabled={busy || !facilityId || !hisId.trim()}>
        {busy ? 'Linking…' : 'Link HIS record'}
      </button>
    </div>
  );

  return embedded ? (
    <div>
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Link another HIS record</div>
      {body}
    </div>
  ) : (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Link a facility HIS record</h2>
      <p className="mb-3 text-xs text-slate-500">
        Creates the Master Patient Index profile for {nin} if it doesn't exist yet.
      </p>
      {body}
    </div>
  );
}
